<!-- This component is used to create a side panel layout.
Expected behaviour on mobile:
- the side panel should slide on top from the left/right of the main content and the main content should not compress.
Expected behaviour on tablet and desktop:
- the side out panel should slide on the left or right of the main content and the main content should compress accordingly to make room for the slide out panel.

Unfortunately, we cannot wrap the entire component in a {#if show} block because the transition when exiting the dom would not work, this would otherwise be the ideal solution using the transition:fly animation.
But, we can't use the native "fly" animation from svelte because it uses underneath the "transform: translate" property which would be ideal for the specified mobile behaviour, but it does not satisfy the tablet/desktop behaviour.
We have to use, in this instance, a transition on a negative "margin-left/right" for tablet and desktop, and a transition on a negative "right/left" for mobile changing the position of the element to "fixed".
 -->
<script lang="ts">
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import { sidePanel } from "$stores/sidePanel";
  import { clickOutside } from "$actions/clickOutside";
  import { getMediaQuery } from "$utils/mediaQuery";
  import { browser } from "$app/environment";
  import { community } from "app/stores/community";
  import cx from "classnames";

  const TRANSITION_DURATION_MS = 180;

  export let slideTo: "left" | "right";
  export let width: number = 380;
  export let show: boolean = false;
  export let withBorder: boolean = true;
  export let withOverlay: boolean = false;
  export let overlayClickOutsideAction: () => void = sidePanel.hide;
  export let backgroundColor: string | undefined = "max-lg:glossy-effect";

  const isDesktop = getMediaQuery("desktop");
  // prevent scrolling on body on mobile when the panel is open
  $: if (browser && !$isDesktop && withOverlay) {
    document.body.style.overflow = show ? "hidden" : "auto";
  }
  $: slideRange = show ? "0" : `-${width}px`;

  // hack to to prevent the component to preserve the width of the side panel on the initial render
  // this seems to happen only on right aside panel since we moved to vite 6
  let hideBeforeMount = slideTo === "left";
  // render the component only when it is visible
  let renderAside = false;
  $: {
    if (show) {
      renderAside = true;
    }
    setTimeout(() => {
      if (!show) {
        renderAside = false;
      }
    }, TRANSITION_DURATION_MS);
  }

  onMount(() => {
    hideBeforeMount = false;
  });
</script>

{#if show && withOverlay && !$isDesktop}
  <div
    transition:fade={{ duration: TRANSITION_DURATION_MS }}
    class="fixed inset-0 z-10 bg-black/30 backdrop-blur-sm"
  />
{/if}

{#if !hideBeforeMount}
  <div
    class="z-modal overscroll-contain ease-out max-lg:fixed max-lg:top-0 max-lg:h-full"
    class:slideLeft={slideTo === "left"}
    class:slideRight={slideTo === "right"}
    style="--slide-range: {slideRange};  --default-slide: -{width}px; transition-duration: {TRANSITION_DURATION_MS}ms"
  >
    <!-- default-slide hides sidepanel on initial render when left-panel-closed is set on document -->
    {#if renderAside}
      <aside
        style="width: {width}px"
        class={cx(
          `${backgroundColor} relative flex h-full w-full max-w-[80vw] flex-col overflow-hidden`,
          {
            "border-black/8 dark:border-white/8": withBorder,
            "border-l-[0.5px]": slideTo === "left",
            "border-r-[0.5px]": slideTo === "right",
          }
        )}
        class:dark:!bg-dark-custom-background={$community.backgroundColorDark}
        use:clickOutside={() => {
          if (!$isDesktop && show) {
            overlayClickOutsideAction();
            return true;
          }
        }}
      >
        <slot />
      </aside>
    {:else}
      <div style="width: {width}px" />
    {/if}
  </div>
{/if}

<style lang="postcss">
  @reference "tailwindcss"; /* this is now needed to use the @variant lg */
  @variant lg {
    .slideLeft {
      margin-right: var(--slide-range);
    }

    .slideRight {
      margin-left: var(--slide-range);
    }
  }

  .slideLeft {
    right: var(--slide-range);
  }

  .slideRight {
    left: var(--slide-range);
  }
</style>
